// Parse + auto-map helpers for the bookings spreadsheet importer.
// Pure functions only: no React, no Supabase, easy to unit-test.

export type DibsField =
  | "booking_date"
  | "booking_time"
  | "customer_name"
  | "customer_email"
  | "customer_phone"
  | "listing_name"
  | "number_of_pax"
  | "total_paid"
  | "status"
  | "cancelled_at"
  | "notes";

export const REQUIRED_FIELDS: DibsField[] = [
  "booking_date",
  "booking_time",
  "customer_name",
  "customer_email",
];

export const ALL_FIELDS: { key: DibsField; label: string; required: boolean }[] = [
  { key: "booking_date", label: "Booking date", required: true },
  { key: "booking_time", label: "Booking time", required: true },
  { key: "customer_name", label: "Customer name", required: true },
  { key: "customer_email", label: "Customer email", required: true },
  { key: "customer_phone", label: "Customer phone", required: false },
  { key: "listing_name", label: "Listing / service", required: false },
  { key: "number_of_pax", label: "Pax / guests", required: false },
  { key: "total_paid", label: "Total paid", required: false },
  { key: "status", label: "Status", required: false },
  { key: "cancelled_at", label: "Cancelled at", required: false },
  { key: "notes", label: "Notes", required: false },
];

// Header synonyms used for auto-detect. All lowercased, punctuation stripped.
const HEADER_HINTS: Record<DibsField, string[]> = {
  booking_date: ["date", "booking date", "appointment date", "appt date", "day", "when"],
  booking_time: ["time", "booking time", "appointment time", "appt time", "slot", "start time"],
  customer_name: ["name", "customer", "customer name", "client", "client name", "guest", "guest name", "full name"],
  customer_email: ["email", "customer email", "client email", "e-mail", "mail"],
  customer_phone: ["phone", "mobile", "customer phone", "client phone", "tel", "telephone", "contact", "whatsapp"],
  listing_name: ["listing", "service", "product", "package", "class", "session", "type", "item"],
  number_of_pax: ["pax", "guests", "people", "headcount", "qty", "quantity", "seats"],
  total_paid: ["paid", "total", "amount", "price", "revenue", "total paid", "fee", "total amount"],
  status: ["status", "state", "outcome"],
  cancelled_at: ["cancelled at", "canceled at", "cancellation date", "cancellation time", "cancelled on", "canceled on", "cancel date", "date cancelled", "cancelled"],
  notes: ["notes", "remark", "remarks", "comment", "comments", "description"],
};

const normalize = (s: string) =>
  s.toLowerCase().replace(/[_\-/\\.]+/g, " ").replace(/\s+/g, " ").trim();

// Given the spreadsheet's header row, propose a best-guess mapping
// of DibsField → header name (or null when no good match).
export function autoMapHeaders(
  headers: string[],
): Record<DibsField, string | null> {
  const used = new Set<string>();
  const out = {} as Record<DibsField, string | null>;
  const normHeaders = headers.map((h) => ({ raw: h, norm: normalize(h) }));

  for (const field of ALL_FIELDS) {
    const hints = HEADER_HINTS[field.key];
    let best: string | null = null;
    let bestScore = 0;

    for (const h of normHeaders) {
      if (used.has(h.raw)) continue;
      // exact match wins
      if (hints.includes(h.norm)) {
        best = h.raw;
        bestScore = 100;
        break;
      }
      // substring fallback
      for (const hint of hints) {
        if (h.norm.includes(hint) || hint.includes(h.norm)) {
          const score = Math.min(h.norm.length, hint.length);
          if (score > bestScore) {
            best = h.raw;
            bestScore = score;
          }
        }
      }
    }

    if (best) used.add(best);
    out[field.key] = best;
  }
  return out;
}

// Date / time normalization
// Convert common spreadsheet date formats to ISO YYYY-MM-DD.
// Accepts: "2026-05-28", "28/05/2026", "05/28/2026" (US),
// "28-May-2026", "May 28, 2026", Excel serial numbers, JS Date.
// Returns null on ambiguity it can't resolve.
export function normalizeDate(
  input: unknown,
  preferDMY = true, // most non-US locales use D/M/Y; default to that
): string | null {
  if (input == null || input === "") return null;
  if (input instanceof Date && !isNaN(input.getTime())) {
    return toISODate(input);
  }
  if (typeof input === "number") {
    // Excel serial date (days since 1899-12-30)
    if (input > 20000 && input < 80000) {
      const ms = (input - 25569) * 86400 * 1000;
      const d = new Date(ms);
      return isNaN(d.getTime()) ? null : toISODate(d);
    }
  }
  const s = String(input).trim();
  if (!s) return null;

  // Already ISO
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);

  // 28/05/2026 or 05/28/2026 or 28-05-2026
  const m1 = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (m1) {
    const a = parseInt(m1[1], 10);
    const b = parseInt(m1[2], 10);
    let y = parseInt(m1[3], 10);
    if (y < 100) y += 2000;
    let day: number, month: number;
    if (preferDMY) {
      day = a;
      month = b;
    } else {
      day = b;
      month = a;
    }
    if (month > 12 && day <= 12) {
      // swap if D/M ordering is impossible
      [day, month] = [month, day];
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${y.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
  }

  // Fall back to JS Date parser (handles "May 28, 2026" etc.)
  const d = new Date(s);
  if (!isNaN(d.getTime())) return toISODate(d);
  return null;
}

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Convert common time strings to HH:MM (24h). Returns null on failure.
// Accepts: "14:30", "2:30 PM", "2:30PM", "14:30:00", Excel time fraction (0.605).
export function normalizeTime(input: unknown): string | null {
  if (input == null || input === "") return null;
  if (typeof input === "number" && input >= 0 && input < 1) {
    const totalMin = Math.round(input * 24 * 60);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  }
  const s = String(input).trim().toLowerCase();
  if (!s) return null;
  // 24h HH:MM or HH:MM:SS
  const m24 = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (m24) {
    const h = parseInt(m24[1], 10);
    const m = parseInt(m24[2], 10);
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  }
  // 12h "2:30 pm" / "2pm"
  const m12 = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/);
  if (m12) {
    let h = parseInt(m12[1], 10);
    const m = m12[2] ? parseInt(m12[2], 10) : 0;
    const pm = m12[3] === "pm";
    if (h < 1 || h > 12 || m < 0 || m > 59) return null;
    if (h === 12) h = pm ? 12 : 0;
    else if (pm) h += 12;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  }
  return null;
}

// Normalize a "date + optional time" cell (e.g. "2026-07-02 09:15",
// "02/07/2026 9:15 AM", "2026-07-02", Excel serial+fraction) to
// "YYYY-MM-DDTHH:MM". Date-only inputs default to 12:00. A midday anchor
// keeps same-day cancellations within the 24h late-cancel window without
// claiming minute precision the source never had. Returns null on failure.
export function normalizeDateTime(input: unknown): string | null {
  if (input == null || input === "") return null;

  // Excel serial with a fractional day carries both date and time.
  if (typeof input === "number" && input > 20000 && input < 80000) {
    const datePart = normalizeDate(Math.floor(input));
    if (!datePart) return null;
    const frac = input - Math.floor(input);
    const time = frac > 0 ? normalizeTime(frac) : null;
    return `${datePart}T${time ?? "12:00"}`;
  }
  if (input instanceof Date && !isNaN(input.getTime())) {
    const datePart = normalizeDate(input);
    if (!datePart) return null;
    const h = input.getHours().toString().padStart(2, "0");
    const m = input.getMinutes().toString().padStart(2, "0");
    return `${datePart}T${h}:${m}`;
  }

  const s = String(input).trim();
  if (!s) return null;
  // Split "date time" on the last run of whitespace (or a T), keeping AM/PM
  // with the time part: "2/7/2026 9:15 am" → ["2/7/2026", "9:15 am"].
  const m = s.match(/^(.+?)[\sT]+(\d{1,2}:\d{2}(?::\d{2})?\s*(?:am|pm)?)$/i);
  if (m) {
    const datePart = normalizeDate(m[1].trim());
    const timePart = normalizeTime(m[2].trim());
    if (datePart && timePart) return `${datePart}T${timePart}`;
    if (datePart) return `${datePart}T12:00`;
    return null;
  }
  const dateOnly = normalizeDate(s);
  return dateOnly ? `${dateOnly}T12:00` : null;
}

// Strip non-digit chars except leading + so phones look uniform.
export function normalizePhone(input: unknown): string | null {
  if (input == null) return null;
  const s = String(input).trim();
  if (!s) return null;
  const cleaned = s.replace(/[^\d+]/g, "");
  return cleaned || null;
}

// Coerce a money string ("$60.00", "60", "1,200.50") to number.
export function normalizeMoney(input: unknown): number | null {
  if (input == null || input === "") return null;
  if (typeof input === "number") return input;
  const cleaned = String(input).replace(/[^0-9.-]/g, "");
  if (!cleaned) return null;
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

export type ParsedRow = Record<string, unknown>;

// Apply the user's column mapping + per-field normalization to produce
// the payload sent to the bookings-import edge function.
export function applyMapping(
  rows: ParsedRow[],
  mapping: Partial<Record<DibsField, string | null>>,
) {
  return rows.map((row, i) => {
    const pick = (k: DibsField) => {
      const col = mapping[k];
      return col ? row[col] : null;
    };
    return {
      row_index: i + 2, // +2 because spreadsheet row 1 is the header
      booking_date: normalizeDate(pick("booking_date")),
      booking_time: normalizeTime(pick("booking_time")),
      customer_name: (pick("customer_name") ?? "").toString().trim() || null,
      customer_email: (pick("customer_email") ?? "").toString().trim() || null,
      customer_phone: normalizePhone(pick("customer_phone")),
      listing_name: (pick("listing_name") ?? "").toString().trim() || null,
      number_of_pax: (() => {
        const v = pick("number_of_pax");
        if (v == null || v === "") return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      })(),
      total_paid: normalizeMoney(pick("total_paid")),
      status: (pick("status") ?? "").toString().trim() || null,
      cancelled_at: normalizeDateTime(pick("cancelled_at")),
      notes: (pick("notes") ?? "").toString().trim() || null,
    };
  });
}
